import { API_HOST } from './api';
import type {
  ImprovementCandidatesResponse,
  ReportTemplate,
  SplitCreateGroupSelection,
  SplitCreatedReport,
  SplitPlanResponse,
  SplitSkippedGroup,
} from '@safetyhub/shared';
import {
  publicTokenRequest as request,
  type CreateSiteContactByTokenInput,
} from './publicTokenApi';

/**
 * COMPOSED 위임 외부 작성 공개 API 클라이언트 (DLR dlr-external 동형).
 *
 * 무인증(토큰 기반) 경로 — axios 인증 인스턴스가 아닌 순수 fetch를 쓴다(인증 헤더 미부착).
 * 서버 계약: apps/server/src/reports/composed-external.controller.ts
 *   GET   /api/v1/public/composed/:token           → 초안 정보(템플릿 스냅샷 + content)
 *   POST  /api/v1/public/composed/:token/split-plan → 묶음 위임 분할 계획(미확장 placeholder 한정)
 *   POST  /api/v1/public/composed/:token/expand    → placeholder 1건 → 그룹 N건 확장
 *   PATCH /api/v1/public/composed/:token/draft     → content 부분 갱신(CREATING/DRAFT/REJECTED)
 *   POST  /api/v1/public/composed/:token/site-contacts → 현장 담당자 등록(토큰당 cap 10)
 *   POST  /api/v1/public/composed/:token/complete  → 작성자 서명 + 결재 자동 시작
 * 공개 경로라 AI 재생성 불가(인증 API 미호출). 옛 flat aiSelect 박제 소비 경로는 2026-07-25 철거.
 *
 * 묶음 위임(§6, 결정 2026-09-06-tbm-post-submit-distribution-and-delegation): 토큰 1개가
 * 확장 산출 N건 전체의 작성 수단이다. get/draft/complete 세 경로가 선택 파라미터 `reportId`로
 * **같은 묶음 안의 대상 문서**를 지정하고(형제 술어 검증은 서버 소관 — 어긋나면 404 균일),
 * 미지정이면 종전대로 토큰 문서(앵커)를 가리킨다.
 */

/**
 * 묶음 형제 1건 — 외부 카운터 k/N·건 간 이동 재료(S11). 서버 `listSplitSiblings` 투영 그대로다.
 *
 * ⚠️ 이 필드의 **부재가 곧 "묶음이 아님"** 이다(서버는 묶음일 때만 싣는다). 확장 전 placeholder와
 * 통짜 위임이 함께 여기로 접히므로, "미확장 placeholder"를 판별하려면 상태(CREATING)와 스냅샷
 * 정책(policy.split) 선언을 함께 봐야 한다.
 */
export interface ComposedExternalSplitSibling {
  reportId: string;
  splitGroupKey: string | null;
  /** 그룹 표시명 — 칩 라벨. 서버가 확장 시 박제한 값(없으면 키로 degrade). */
  splitGroupLabel: string | null;
  /** ReportStatus 문자열. 작성 미완 판정 축(CREATING = 아직 안 씀). */
  status: string;
  /** 토큰이 앵커된 문서(그룹 1 = placeholder in-place 채움분)인가. */
  isTokenAnchor: boolean;
  /**
   * 담당자가 **이 문서에** 저장한 적이 있는가(2026-09-10) — 칩 3값의 「작성 중」 축.
   * `status`는 완료 전까지 CREATING이라 그것만으로는 손댄 문서를 구별할 수 없다.
   * 옛 응답엔 없다(undefined) — 그때는 종전 2값으로 degrade한다.
   */
  hasDelegateDraft?: boolean;
}

/** 묶음 확장 결과 — 그룹 1은 placeholder in-place라 `created`에 없다(placeholderReportId가 그것). */
export interface ComposedExternalExpandResult {
  placeholderReportId: string;
  placeholderGroupKey: string;
  created: SplitCreatedReport[];
  skipped: SplitSkippedGroup[];
}

export interface ComposedExternalDraftResponse {
  tokenId: string;
  reportId: string;
  reportTitle: string;
  templateKey: string | null;
  contactName: string;
  expiresAt: string;
  reportStatus: string;
  /**
   * 최신 반려 사유 — **반려자가 위임자 본인일 때만** 서버가 싣는다(R5-①, 2026-08-28).
   * 내부 검토자(안전관리자·기타 결재자)의 반려 사유는 응답에 아예 실리지 않는다(S44) —
   * 조건부 렌더가 아니라 서버 게이트가 정본이다. null이면 상태 안내만 표시한다.
   */
  rejectionReason: string | null;
  /**
   * 관리자 메시지 — 활성 토큰의 최신 발송(위임 생성·재전송 모달)에 위임자가 직접 쓴 안내문.
   * 항상 위임자 글이므로 반려자 축과 무관하게 표시한다. 위임자 본인 반려로 토큰이 재활성화되면
   * 서버가 지난 라운드 문구를 지워 null이 된다(반려 사유와의 병존 차단). 3자 반려는 토큰을
   * 건드리지 않고 위임자의 재요청이 새 토큰(새 message)을 만든다(F-31, 2026-09-11).
   */
  message: string | null;
  /** 담당자 저장분 + 서버 박제분(prefill 기본값). null이면 렌더 불가로 처리. */
  content: Record<string, unknown> | null;
  /**
   * 렌더/작성용 템플릿 스냅샷(위임 생성 시점 박제) — body/composerWidgetTree/
   * dataDefs/dataSources/delegationMeta 포함. id/enabled/timestamps는 제외된 부분 형상이라
   * deriveWriteForm/작성폼 렌더에 필요한 필드만 신뢰한다.
   */
  template: ReportTemplate;
  site: { id: string; name: string };
  company: { id: string; name: string };
  /**
   * AI 개선 의견 후보 — 위임 생성 시(인증 위임자 컨텍스트) 사전 생성분의 **캐시 히트 한정** 동봉.
   * 미스면 서버가 필드를 생략(무인증 경로 AI 생성 없음 — 2026-07-26 위임 흐름 배선).
   * 페이지가 improvementCandidatesToRows로 파트별 rows를 만들어 resolvedRows로 주입한다.
   */
  improvementCandidates?: ImprovementCandidatesResponse;
  /**
   * 작성폼 콤보 제안 소스 rows — 템플릿(스냅샷)의 combobox/sortableSelect가 optionsSources로
   * 참조하는 **카탈로그 list 소스**만 서버가 동봉한다(일반 축, 보고서 type 하드코딩 없음).
   * 키는 카탈로그 키(`site-contacts`·`site-locations` 등)이고 rows 형상은 인증 prefill 값 채널
   * (result[key]=rows)과 동일 — 페이지가 그대로 resolvedRows로 주입한다.
   * 참조 소스가 없거나 전부 미해소면 서버가 필드를 생략(콤보는 rows 없으면 일반 입력 degrade).
   */
  catalogRows?: Record<string, Record<string, unknown>[]>;
  /**
   * 카탈로그 rows resolve **실패** 신호 — 템플릿이 카탈로그 소스를 참조하는데 서버가 rows를
   * 만들지 못했을 때만 true(성공·참조 없음이면 생략 — 서버 계약 2026-08-24). 페이지는 이때
   * requireSelection 콤보를 자유 입력으로 강등해 "골라야만 하는데 고를 목록이 없는" 교착을 푼다.
   */
  catalogRowsUnavailable?: boolean;
  /**
   * 이 문서가 담당하는 분할 그룹 — **null이면 아직 그룹 미선택**(또는 통짜 위임)이다.
   *
   * 그룹 선택 게이트(`needsGroupSelection`)의 축이다. 종전엔 `splitSiblings` **부재**로 대리
   * 판정했는데, 배치 합류 개정이 「미확장인데 형제가 있다」는 형상을 처음 만들자 그 대리가
   * 무너져 담당자가 그룹 선택 없이 작성 폼으로 직행했다(2026-09-10 실사고). **남의 목록으로
   * 자기 상태를 추론하지 않는다** — 서버가 늘 싣는 자기 필드로 판정한다.
   */
  splitGroupKey?: string | null;
  /**
   * 묶음 위임 형제 목록(앵커 포함, 생성 순) — **묶음일 때만** 서버가 싣는다(§6).
   * 통짜 위임·확장 전 placeholder는 필드 자체가 없다(ComposedExternalSplitSibling doc).
   */
  splitSiblings?: ComposedExternalSplitSibling[];
}

const BASE = `${API_HOST}/api/v1/public/composed`;

/**
 * 초안 조회 — `reportId`를 주면 같은 묶음의 그 형제를, 안 주면 토큰 문서(앵커)를 돌려준다.
 * 형제 술어(같은 회사·템플릿·점검 세트·담당자·분할 산물) 검증은 서버 소관이고, 어긋나면
 * 404가 난다 — 클라가 소속을 판정하지 않는다.
 */
export async function getComposedDraft(
  token: string,
  reportId?: string,
): Promise<ComposedExternalDraftResponse> {
  const query = reportId ? `?reportId=${encodeURIComponent(reportId)}` : '';
  return request<ComposedExternalDraftResponse>(`${BASE}/${token}${query}`);
}

/** `GET :token/siblings` 응답 — `splitSiblings`는 초안 조회의 같은 필드와 동일 형상(묶음 아니면 null). */
export interface ComposedExternalSiblingsResponse {
  /** 토큰 앵커 문서 id(재앵커 뒤엔 살아남은 형제) */
  reportId: string;
  splitSiblings: ComposedExternalSplitSibling[] | null;
}

/**
 * 형제 목록만 — 토큰 방(`/ws/public`) 이벤트·재연결 뒤 재조회용
 * (결정 2026-09-10-public-token-websocket-room §3).
 *
 * `getComposedDraft`로 재조회하지 않는 이유: 본문 전체가 내려와 **입력 중인 값을 덮고**,
 * 그 조회는 「첫 열람」 표식도 찍는다. 토큰이 죽었으면 REST와 같은 코드(REPORT_NOT_FOUND /
 * VALIDATION_INVALID_INPUT)로 실패한다 — 호출부는 그것을 만료로 접는다.
 */
export async function getComposedSiblingsByToken(
  token: string,
): Promise<ComposedExternalSiblingsResponse> {
  return request<ComposedExternalSiblingsResponse>(`${BASE}/${token}/siblings`);
}

export async function updateComposedDraft(
  token: string,
  content: Record<string, unknown>,
  reportId?: string,
): Promise<void> {
  await request<{ saved: boolean }>(`${BASE}/${token}/draft`, {
    method: 'PATCH',
    // reportId는 서버 DTO가 optional로 받는다 — 없는 값을 실어 보내지 않는다(whitelist 계약).
    body: JSON.stringify(reportId ? { content, reportId } : { content }),
  });
}

/**
 * 묶음 위임 분할 계획 조회(§6 ③) — 그룹 선택 화면의 재료.
 *
 * ⚠️ **POST인 이유가 부수효과다**(서버 `getPlan` JSDoc: provider 호출이 TbmMeta upsert·AI 정제
 * 캐시를 수반). 화면 진입의 **명시적 조작 1회로만** 부를 것 — 페이지 로드마다 자동 호출하면
 * 새로고침이 그대로 부수효과가 된다.
 *
 * ⚠️ `plan: null`은 오류가 아니라 "이 컨텍스트는 분할하지 않는다"이다(인증 경로 `getSplitPlan`과
 * 같은 계약). 호출부는 그룹 선택 없이 통짜 작성으로 가야 한다(§6 ②).
 */
export async function getComposedSplitPlanByToken(
  token: string,
): Promise<SplitPlanResponse> {
  return request<SplitPlanResponse>(`${BASE}/${token}/split-plan`, { method: 'POST' });
}

/**
 * 묶음 확장(§6 ③④) — placeholder 1건을 담당자가 고른 그룹 N건으로.
 *
 * 요청에 식별자(회사·현장·템플릿·점검)를 싣지 않는다 — 전부 토큰의 placeholder에서 파생한다
 * (@Public 쓰기 최소화). `groups[0]`이 placeholder in-place 채움분이라 **순서가 의미를 갖는다**.
 * 재확장·경합 패자는 409(`REPORT_CREATE_DUPLICATE`).
 */
export async function expandComposedByToken(
  token: string,
  groups: SplitCreateGroupSelection[],
): Promise<ComposedExternalExpandResult> {
  return request<ComposedExternalExpandResult>(`${BASE}/${token}/expand`, {
    method: 'POST',
    body: JSON.stringify({ groups }),
  });
}

/**
 * 공개 담당자 등록 입력 — 서버 DTO(CreateSiteContactByTokenDto)와 형상 동일(whitelist 계약).
 * 정본은 publicTokenApi(DLR 공개 경로와 공유) — 기존 이름 유지를 위한 별칭.
 */
export type CreateComposedSiteContactInput = CreateSiteContactByTokenInput;

/**
 * 등록 결과 — **카탈로그 rows 행 형상 투영**(서버 계약 2026-08-24: mapSiteContactRows와 같은
 * 파생 표시 필드 산식 소유는 서버). phone은 노출하지 않는다(카탈로그 rows에도 없음).
 * 페이지는 이 행을 재조회 없이 해당 catalogKey rows에 그대로 병합한다.
 */
export interface ComposedExternalSiteContact {
  id: string;
  name: string;
  department: string;
  position: string;
  /** 레거시 파생 표시(`이름 - 부서 (직책)`) — 신규 저작 조합 표시는 클라이언트 조합 템플릿 담당 */
  display: string;
}

/**
 * 토큰으로 현장 담당자(SiteContact) 등록 — DLR 공개 경로와 같은 DTO·코어(cap 10).
 * siteId/companyId는 서버가 토큰에서만 파생하므로 입력에 식별자를 싣지 않는다.
 */
export async function createComposedSiteContactByToken(
  token: string,
  input: CreateComposedSiteContactInput,
): Promise<ComposedExternalSiteContact> {
  return request<ComposedExternalSiteContact>(`${BASE}/${token}/site-contacts`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function completeComposedDraft(
  token: string,
  params: {
    signatureData: string;
    signerName: string;
    signerTitle?: string;
    /** 묶음 위임: 완료할 형제 문서. 미지정이면 토큰 문서(앵커). */
    reportId?: string;
  },
): Promise<void> {
  await request<{ completed: boolean; reportId: string }>(`${BASE}/${token}/complete`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}
